<script lang="ts" setup>
  import { useFormContext, getModelFromContext, setModelWithContext } from '@othercommerce/foundation';
  import { useId, computed } from 'vue';

  interface Props {
    id?: string | undefined,
    name?: string | undefined,
    label?: string | undefined,
    required?: boolean,
    readonly?: boolean,
    disabled?: boolean,
    reverse?: boolean,
  }

  defineOptions({
    inheritAttrs: false,
  });

  const emit = defineEmits([
    'update:modelValue',
  ]);

  const props = withDefaults(defineProps<Props>(), {
    required: false,
    readonly: false,
    disabled: false,
    reverse: false,
  });

  const context = useFormContext();
  const random = useId();

  const model = defineModel({
    get: (value) => getModelFromContext(props.name, context, value),
    set: (value) => setModelWithContext(props.name, context, value),
  });

  const id = computed(() => {
    return props.id ?? random;
  });

  const disabled = computed(() => {
    // Checkboxes may have only a disabled state, for compatibility we treat a readonly as disabled here.
    return Boolean(props.disabled || props.readonly || (context && context.readonly.value));
  });

  const classes = computed(() => {
    return {
      'switch--reverse': props.reverse,
    };
  });
</script>
<template>
  <SfControlWrapper v-bind="{ id, name, required }" class="control--switch" v-slot="{errorId}">
    <label class="switch-wrapper" :class="classes" :for="id">
      <input class="switch-input" type="checkbox" v-bind="{ id, name, disabled, ...$attrs }" v-model="model" :aria-describedby="errorId" />
      <span class="control switch-btn" />
      <span v-if="label || $slots.default" class="switch-label">
        <slot>{{ label }}</slot>
      </span>
    </label>
  </SfControlWrapper>
</template>
<style scoped lang="scss">
  .switch-wrapper {
    display: flex;
    margin: var(--sf-switch-margin-y) var(--sf-switch-margin-x);
    gap: calc(var(--sf-switch-line-height) / 2);
    cursor: pointer;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;

    &.switch--reverse {
      flex-direction: row-reverse;
    }

    .switch-btn {
      position: relative;
      display: inline-block;
      flex-shrink: 0;
      height: var(--sf-switch-line-height);
      width: calc(var(--sf-switch-line-height) * 2);
      border-radius: calc(var(--sf-switch-line-height) / 2);
      padding: 0;
      border: none;
      background-color: var(--sf-switch-background-color);

      &:after {
        content: "";
        position: absolute;
        top: 2px;
        left: 2px;
        height: calc(var(--sf-switch-line-height) - 4px);
        width: calc(var(--sf-switch-line-height) - 4px);
        background-color: var(--sf-switch-btn-background-color);
        border-radius: calc(var(--sf-switch-line-height) / 2);
        transition-property: left, transform, background-color;
        transition-duration: var(--sf-input-transition-duration);
        transition-timing-function: var(--sf-input-transition-timing-func);
      }
    }

    .switch-input {
      position: absolute;
      opacity: 0;
      height: 0;
      width: 0;

      &:focus-visible:not(:disabled) ~ .switch-btn {
        outline: 2px solid var(--sf-input-border-focus);
        outline-offset: 2px;
      }

      &:active:not(:disabled) ~ .switch-btn {
        background-color: var(--sf-switch-background-color-active);
      }

      &:disabled {
        & ~ .switch-btn {
          pointer-events: none;
          opacity: 0.7;
        }

        & ~ .switch-label {
          pointer-events: none;
          opacity: 0.7;
        }
      }

      &:checked ~ .switch-btn {
        background-color: var(--sf-switch-background-color-checked);

        &:after {
          left: calc(100% - 2px);
          transform: translateX(-100%);
          background-color: var(--sf-switch-btn-background-color-checked);
        }
      }
    }

    .switch-label {
      display: inline-block;
      text-align: left;
      font-size: var(--sf-switch-font-size);
      line-height: var(--sf-switch-line-height);
      font-weight: var(--sf-switch-label-weight);
      text-transform: var(--sf-switch-label-text-transform);
      color: var(--sf-switch-label-color);
    }
  }
</style>
